/**
 * 時間の錨 (TimeAnchor) の計算 (docs/features/video-editor-timeline-lineage-refactoring-plan.md §4)。
 *
 * ナレーション (narrationTrack) / 効果音 (seTrack) / BGM (bgmTrack) / 動画全体のオーバーレイ (overlays) は
 * 「動画全体の絶対秒」で置かれているが、その**正は錨** (`anchor` / `anchorEnd`。schema/timeAnchorSchema.ts) で、
 * 絶対秒 (`startSec` / `endSec`) は錨から引き直したキャッシュ。タイムラインが変わるたびに 1 つの規則
 * (`recomputeAnchoredTiming`。§4.2) で引き直す。レンダラーと書き出しは絶対秒だけを読む。
 *
 * - `anchorForSec` … 絶対秒を錨に分解する (§4.3 の「書く」経路・§4.7 の旧データ移行)。境界の帰属は
 *   edge で決める: 開始 (`'start'`) は境界ちょうどを**後ろ**の項目へ、終端 (`'end'`) は**前**の項目へ
 * - `recomputeAnchoredTiming` … 錨から秒を引き直す (べき等)。`silenced` (ナレーション / SE が鳴らない) も
 *   ここで導出する。錨への書き込みは削除時の `absolute` 退避 (§4.5) だけ
 * - `migrateLegacyAnchors` … 錨の無い cue に、保存されている絶対秒から錨を補う (§4.7)
 * - `syncEditedCueAnchors` … UI / 部分操作で絶対秒を直接書き換えた cue の錨を、今のタイムラインで置き直す
 *   (§4.3 の 1 行目)
 * - `remapAnchorsForTimelineChange` … 分割 / 結合 / 区切り位置の変更で項目が増減したとき、錨を「素材秒を
 *   含む同じインスタンスの項目」へ付け替える (§4.2 末尾の分割・結合の規則を 1 つにしたもの)
 *
 * backend の `video_edit/time_anchor.py` と同じ規則 (FE / BE 同値)。
 */
import { computeTimelineLayout } from '../remotion/layout';
import { minAudioCueSec } from '../audio/audioCue';
import { timeAnchorsEqual, type TimeAnchor } from './timeAnchorSchema';
import {
  itemDisplayDurationSec,
  itemDisplaySecAtSource,
  itemSourceSecAt,
  roundSec,
  type EditProject,
  type TimelineItem,
} from './project';

/** 錨を持つ要素の種類。要素別の規則 (§4.2 手順 5〜7) の分岐にだけ使う。 */
export type AnchoredKind = 'narration' | 'se' | 'bgm' | 'overlay';

/** 錨を持つ要素の共通部分 (NarrationCue / AudioCue / Overlay の構造的な部分型)。 */
export interface AnchoredCue {
  startSec?: number;
  endSec?: number | null;
  anchor?: TimeAnchor;
  anchorEnd?: TimeAnchor;
  sourceRemoved?: boolean;
  silenced?: boolean;
}

export type AnchorEdge = 'start' | 'end';

/** 採用中の項目 1 つの表示区間 (動画全体の秒。レイアウト = フレーム積みから戻した値)。 */
export interface AnchorSpan {
  itemId: string;
  startSec: number;
  endSec: number;
  /** 表示尺 (= endSec - startSec。フレームへ丸めた尺)。 */
  displaySec: number;
}

/** 錨の解決に要るタイムラインの情報。 */
export interface AnchorTimeline {
  /** 採用中の項目の区間 (タイムライン順)。 */
  spans: AnchorSpan[];
  spanById: Map<string, AnchorSpan>;
  /** タイムラインにある全項目の id (除外中も含む。削除と除外の区別に使う)。 */
  itemIds: Set<string>;
  /** 最後の採用中の項目の終了秒 (= 動画の終わり)。採用中が無ければ 0。 */
  totalEndSec: number;
  /** 出力 fps (境界の許容差の計算に使う。0 なら不明)。 */
  fps: number;
}

/** タイムラインの区間を組む。素材の参照切れ等でレイアウトが組めないときは null (追従は行わない)。 */
export function anchorTimelineOf(project: EditProject): AnchorTimeline | null {
  let layout: ReturnType<typeof computeTimelineLayout>;
  try {
    layout = computeTimelineLayout(project);
  } catch {
    return null;
  }
  const fps = project.output.fps;
  const spans: AnchorSpan[] = layout.map((entry) => {
    const startSec = entry.startFrame / fps;
    const displaySec = entry.durationFrames / fps;
    return { itemId: entry.item.id, startSec, endSec: startSec + displaySec, displaySec };
  });
  let totalEndSec = 0;
  for (const span of spans) totalEndSec = Math.max(totalEndSec, span.endSec);
  return {
    spans,
    spanById: new Map(spans.map((span) => [span.itemId, span])),
    itemIds: new Set(project.timeline.map((item) => item.id)),
    totalEndSec,
    fps: fps > 0 ? fps : 0,
  };
}

const EPS = 1e-9;

/**
 * 絶対秒を境界と比べるときの許容差 = 半フレーム (fps 不明なら 0.005 秒)。
 * cue の秒は小数 2 桁に丸めて保存される (roundSec) 一方、シーン境界はフレーム積み (1/24 秒など) なので、
 * 「シーンの頭」に置いた cue が境界より最大 0.005 秒手前 / 後ろにずれて保存されている。許容差なしで分解すると
 * 前のシーンの末尾 (offset = 表示尺 − 0.002 など) に付いてしまい、並べ替えで前のシーンに付いて行く
 * (実機確認 2026-11-02: 旧データのナレーションが全てこの形だった)。
 */
export function boundaryToleranceSec(fps: number): number {
  return fps > 0 ? 0.5 / fps : 0.005;
}

/**
 * 絶対秒 `sec` を錨に分解する (§4.3 / §4.7)。
 * - 採用中の項目の区間内なら `item` (境界ちょうどは edge で決める: 開始は後ろの項目・offset 0、終端は前の項目・
 *   offset = その項目の表示尺)。境界から半フレーム以内 (`boundaryToleranceSec`) は境界ちょうどとして扱う
 * - 最後の項目の終わり以降なら `tail` (終端で「終わりちょうど」は最後の項目に属する)
 * - 採用中の項目が無ければ `absolute`
 */
export function anchorForSec(tl: AnchorTimeline, sec: number, edge: AnchorEdge): TimeAnchor {
  const spans = tl.spans;
  const last = spans[spans.length - 1];
  if (!last) return { kind: 'absolute' };
  const tol = boundaryToleranceSec(tl.fps);
  if (edge === 'start' ? sec >= last.endSec - tol : sec > last.endSec + tol) {
    return { kind: 'tail', offsetSec: roundSec(Math.max(0, sec - last.endSec)) };
  }
  let k = -1;
  for (let i = 0; i < spans.length; i += 1) {
    const s = spans[i]!.startSec;
    if (edge === 'start' ? s <= sec + tol : s < sec - tol) k = i;
  }
  if (k < 0) return { kind: 'item', itemId: spans[0]!.itemId, offsetSec: 0 };
  const span = spans[k]!;
  const offset = Math.min(span.displaySec, Math.max(0, sec - span.startSec));
  return { kind: 'item', itemId: span.itemId, offsetSec: roundSec(offset) };
}

export type AnchorState = 'OK' | 'EXCLUDED' | 'DELETED' | 'NONE';

/**
 * 錨を今のタイムラインで秒に解決する (§4.2 手順 2 / 3)。錨は読むだけ。
 * `prev` は引き直し前の保存値 (absolute・除外・削除のときにそのまま返す)。
 */
export function resolveAnchor(
  anchor: TimeAnchor | undefined,
  tl: AnchorTimeline,
  prev: number | undefined,
): { state: AnchorState; sec: number | undefined } {
  if (!anchor) return { state: 'NONE', sec: prev };
  if (anchor.kind === 'absolute') return { state: 'OK', sec: prev };
  if (anchor.kind === 'tail') return { state: 'OK', sec: tl.totalEndSec + anchor.offsetSec };
  const span = tl.spanById.get(anchor.itemId);
  if (span) {
    return { state: 'OK', sec: span.startSec + Math.min(span.displaySec, Math.max(0, anchor.offsetSec)) };
  }
  return { state: tl.itemIds.has(anchor.itemId) ? 'EXCLUDED' : 'DELETED', sec: prev };
}

/** 要素の最短区間 (§4.2 手順 6 の `minLen`)。現行 `minAudioCueSec` / オーバーレイ 0.01 秒と同じ。 */
function minLenSec(kind: AnchoredKind): number {
  if (kind === 'overlay') return 0.01;
  return minAudioCueSec(kind === 'se' ? 'se' : 'bgm');
}

function sameNumber(a: number | null | undefined, b: number | null | undefined): boolean {
  if (a == null || b == null) return a == null && b == null;
  return Math.abs(a - b) < 1e-9;
}

/** 引き直しで変わりうる欄だけを比べる (変化が無ければ元の参照を返して再描画を増やさないため)。 */
function sameAnchoredTiming(a: AnchoredCue, b: AnchoredCue): boolean {
  return (
    sameNumber(a.startSec, b.startSec) &&
    sameNumber(a.endSec, b.endSec) &&
    (a.silenced ?? undefined) === (b.silenced ?? undefined) &&
    timeAnchorsEqual(a.anchor, b.anchor) &&
    timeAnchorsEqual(a.anchorEnd, b.anchorEnd)
  );
}

/**
 * 1 つの cue の秒を錨から引き直す (§4.2 の手順 1〜7)。変化が無ければ同じ参照を返す。
 */
export function recomputeAnchoredCue<C extends AnchoredCue>(cue: C, kind: AnchoredKind, tl: AnchorTimeline): C {
  const isOverlay = kind === 'overlay';
  // 1. 更新前の値を先に読む
  const startOmitted = isOverlay && typeof cue.startSec !== 'number';
  const prevStart = startOmitted ? 0 : (cue.startSec as number);
  const prevEnd = typeof cue.endSec === 'number' ? cue.endSec : undefined;
  const prevLen = prevEnd !== undefined ? prevEnd - prevStart : undefined;

  const next: C = { ...cue };
  // 始終端とも省略の全編オーバーレイは素通り (錨なし・値を書かない・silenced なし)
  if (isOverlay && startOmitted && prevEnd === undefined) {
    delete next.silenced;
    return sameAnchoredTiming(cue, next) ? cue : next;
  }

  // 2 / 3. 開始と終端の解決 (錨は読むだけ)
  const startResolved = resolveAnchor(cue.anchor, tl, prevStart);
  const endResolved = resolveAnchor(cue.anchorEnd, tl, prevEnd);
  let startState = startResolved.state;
  let endState = endResolved.state;
  let start = startResolved.sec;
  const end = endResolved.sec;
  if (startOmitted && startState === 'NONE') start = 0;

  // 4. 削除による錨の退避 (BGM / 全体オーバーレイだけ。§4.5)。この回の値には prevStart / prevEnd を使う
  if (kind === 'bgm' || isOverlay) {
    if (startState === 'DELETED') {
      next.anchor = { kind: 'absolute' };
      startState = 'OK';
    }
    if (endState === 'DELETED') {
      next.anchorEnd = { kind: 'absolute' };
      endState = 'OK';
    }
  }

  // 5. 値の書き込み (比較・補正は解決済みの数値 effectiveStart で行う)
  const effectiveStart = roundSec(Math.max(0, start ?? prevStart));
  if (!startOmitted) next.startSec = effectiveStart;
  if (prevEnd !== undefined && prevLen !== undefined) {
    if (kind === 'se') next.endSec = roundSec(effectiveStart + prevLen);
    else next.endSec = roundSec(end ?? prevEnd);
  }

  // 6. 区間の整合性 (逆転・潰れは長さを保つ。anchorEnd は触らない。startSec の省略は維持)
  if (typeof next.endSec === 'number' && prevLen !== undefined && next.endSec - effectiveStart < minLenSec(kind) - EPS) {
    next.endSec = roundSec(effectiveStart + prevLen);
  }

  // 7. 無効化 (要素別)
  if (kind === 'narration' || kind === 'se') {
    const silenced = startState === 'EXCLUDED' || startState === 'DELETED' || cue.sourceRemoved === true;
    if (silenced) next.silenced = true;
    else delete next.silenced;
  } else {
    delete next.silenced;
  }
  return sameAnchoredTiming(cue, next) ? cue : next;
}

function mapTrack<C extends AnchoredCue>(track: C[] | undefined, kind: AnchoredKind, tl: AnchorTimeline): C[] | undefined {
  if (!track) return track;
  let changed = false;
  const next = track.map((cue) => {
    const out = recomputeAnchoredCue(cue, kind, tl);
    if (out !== cue) changed = true;
    return out;
  });
  return changed ? next : track;
}

/**
 * プロジェクトの全 cue の秒を錨から引き直す (§4.2)。タイムラインが変わったら 1 回呼ぶ
 * (FE: updateProject の末尾・validateProject の正規化。BE: apply_edit_ops の末尾・保存の正規化)。
 * 同じ入力で繰り返し実行しても出力は変わらない (べき等)。変化が無ければ同じ参照を返す。
 *
 * 採用中の項目が 1 つも無い (配置が空) ときは引き直さず値を保ち、`item` 錨のナレーション / SE だけ
 * `silenced` にする (§4.4)。レイアウトが組めない (素材の参照切れ等) ときは何もしない。
 */
export function recomputeAnchoredTiming(project: EditProject): EditProject {
  const tl = anchorTimelineOf(project);
  if (!tl) return project;
  if (tl.spans.length === 0) {
    const silence = <C extends AnchoredCue>(track: C[] | undefined): C[] | undefined => {
      if (!track) return track;
      let changed = false;
      const next = track.map((cue) => {
        const silenced = cue.anchor?.kind === 'item' || cue.sourceRemoved === true;
        if ((cue.silenced ?? undefined) === (silenced ? true : undefined)) return cue;
        changed = true;
        const out = { ...cue };
        if (silenced) out.silenced = true;
        else delete out.silenced;
        return out;
      });
      return changed ? next : track;
    };
    const narrationTrack = silence(project.narrationTrack);
    const seTrack = silence(project.seTrack);
    if (narrationTrack === project.narrationTrack && seTrack === project.seTrack) return project;
    return {
      ...project,
      ...(project.narrationTrack !== undefined ? { narrationTrack } : {}),
      ...(project.seTrack !== undefined ? { seTrack } : {}),
    };
  }
  const narrationTrack = mapTrack(project.narrationTrack, 'narration', tl);
  const seTrack = mapTrack(project.seTrack, 'se', tl);
  const bgmTrack = mapTrack(project.bgmTrack, 'bgm', tl);
  const overlays = mapTrack(project.overlays, 'overlay', tl);
  if (
    narrationTrack === project.narrationTrack &&
    seTrack === project.seTrack &&
    bgmTrack === project.bgmTrack &&
    overlays === project.overlays
  ) {
    return project;
  }
  return {
    ...project,
    ...(project.narrationTrack !== undefined ? { narrationTrack } : {}),
    ...(project.seTrack !== undefined ? { seTrack } : {}),
    ...(project.bgmTrack !== undefined ? { bgmTrack } : {}),
    ...(project.overlays !== undefined ? { overlays } : {}),
  };
}

/**
 * 錨の無い cue に、保存されている絶対秒から錨を補う (旧データの移行。§4.7)。
 * - 数値の `startSec` → `anchorForSec(..., 'start')`
 * - BGM / 全体オーバーレイの数値の `endSec` → `anchorForSec(..., 'end')` を `anchorEnd` に
 * - 全体オーバーレイで `startSec` が省略されているものは錨を付けない (動画の先頭から。省略のまま)
 * `includeOverlays` が false のときは overlays を見ない (formatVersion 2 の保険経路。§4.7)。
 * 補った件数も返す。変化が無ければ同じ参照を返す。
 */
export function migrateLegacyAnchors(
  project: EditProject,
  options: { includeOverlays: boolean },
): { project: EditProject; filled: number } {
  const tl = anchorTimelineOf(project);
  if (!tl) return { project, filled: 0 };
  let filled = 0;
  const fill = <C extends AnchoredCue>(track: C[] | undefined, kind: AnchoredKind): C[] | undefined => {
    if (!track) return track;
    let changed = false;
    const next = track.map((cue) => {
      let out: C | null = null;
      if (!cue.anchor && typeof cue.startSec === 'number') {
        out = { ...cue, anchor: anchorForSec(tl, cue.startSec, 'start') };
        filled += 1;
      }
      if ((kind === 'bgm' || kind === 'overlay') && !cue.anchorEnd && typeof cue.endSec === 'number') {
        out = { ...(out ?? cue), anchorEnd: anchorForSec(tl, cue.endSec, 'end') };
        filled += 1;
      }
      if (out) changed = true;
      return out ?? cue;
    });
    return changed ? next : track;
  };
  const narrationTrack = fill(project.narrationTrack, 'narration');
  const seTrack = fill(project.seTrack, 'se');
  const bgmTrack = fill(project.bgmTrack, 'bgm');
  const overlays = options.includeOverlays ? fill(project.overlays, 'overlay') : project.overlays;
  if (filled === 0) return { project, filled };
  return {
    project: {
      ...project,
      ...(project.narrationTrack !== undefined ? { narrationTrack } : {}),
      ...(project.seTrack !== undefined ? { seTrack } : {}),
      ...(project.bgmTrack !== undefined ? { bgmTrack } : {}),
      ...(project.overlays !== undefined ? { overlays } : {}),
    },
    filled,
  };
}

/**
 * 絶対秒を直接書き換えた cue (UI のドラッグ / ▲▼ / インスペクタの秒入力・部分操作の絶対秒指定) の錨を、
 * **今のタイムライン** (`next`。操作直後の状態) で置き直す (§4.3 の 1 行目)。
 * - `prev` に同じ id の cue があり、`startSec` が変わったのに `anchor` が同じ → 新しい秒で `anchor` を分解し直す
 * - 同様に `endSec` (BGM / 全体オーバーレイ) が変わったのに `anchorEnd` が同じ → 分解し直す。
 *   数値から null / 省略に変わったら `anchorEnd` を除去、null / 省略から数値に変わったら新しく分解する
 * - `prev` に無い cue (追加されたもの) で錨が無ければ、今の秒で分解する。錨があっても引き直した秒と 1 フレーム以上
 *   違えば (既存 cue を写して秒だけ変えたもの) 今の秒で分解し直す
 * タイムラインだけが変わった (秒は同じ) cue は触らない (引き直しは `recomputeAnchoredTiming`)。
 */
export function syncEditedCueAnchors(prev: EditProject, next: EditProject): EditProject {
  const tl = anchorTimelineOf(next);
  if (!tl) return next;
  const fps = next.output.fps;
  // タイムラインが変わったときだけ、リップル由来の秒の変化を見分けるために変更前の区間も組む
  const prevTl = prev.timeline === next.timeline ? tl : anchorTimelineOf(prev);
  const sync = <C extends AnchoredCue & { id: string }>(
    prevTrack: C[] | undefined,
    track: C[] | undefined,
    kind: AnchoredKind,
  ): C[] | undefined => {
    if (!track) return track;
    const prevById = new Map((prevTrack ?? []).map((cue) => [cue.id, cue]));
    let changed = false;
    const out = track.map((cue) => {
      const before = prevById.get(cue.id);
      let result: C | null = null;
      const hasEnd = kind === 'bgm' || kind === 'overlay';
      if (!before) {
        // 追加された cue: 錨が無ければ今の秒で分解する。錨付きで足されたが (既存 cue を写して秒だけ変えた等)
        // 錨を引き直した秒と 1 フレーム以上違うときも、UI が書いた秒を正として分解し直す
        const frame = fps > 0 ? 1 / fps : 0.05;
        const disagrees = (anchor: TimeAnchor | undefined, sec: number) => {
          if (!anchor) return true;
          const resolved = resolveAnchor(anchor, tl, undefined).sec;
          return resolved === undefined || Math.abs(resolved - sec) > frame + 1e-6;
        };
        if (typeof cue.startSec === 'number' && disagrees(cue.anchor, cue.startSec)) {
          result = { ...cue, anchor: anchorForSec(tl, cue.startSec, 'start') };
        }
        if (hasEnd && typeof cue.endSec === 'number' && disagrees(cue.anchorEnd, cue.endSec)) {
          result = { ...(result ?? cue), anchorEnd: anchorForSec(tl, cue.endSec, 'end') };
        }
      } else {
        // 秒が変わっていても、それがタイムラインの変更に追従した結果 (applyTimelineWithRipple の引き直し) なら
        // 利用者の秒の書き換えではないので触らない。再分解すると、トリムで clamp された錨 (offset > 表示尺) や
        // 項目の末尾の錨が次の項目の頭に置き直されて追従先が変わってしまう (実機確認 2026-11-02)。
        // リップル由来と見なす条件は「錨を変更前後のタイムラインで引き直した秒が実際に動いていて、今の秒が
        // 引き直した秒と 1 フレーム以内」。秒の差が小さいことだけでは判定しない (タイムラインが変わっていない
        // ときの 1 フレーム以内の手動の微調整まで取り消してしまう。レビュー指摘 2026-11-02)
        const frame = fps > 0 ? 1 / fps : 0.05;
        const explainedByAnchor = (anchor: TimeAnchor | undefined, sec: number) => {
          if (!anchor || !prevTl || prevTl === tl) return false;
          const resolved = resolveAnchor(anchor, tl, undefined).sec;
          const resolvedBefore = resolveAnchor(anchor, prevTl, undefined).sec;
          if (resolved === undefined || resolvedBefore === undefined) return false;
          if (Math.abs(resolved - resolvedBefore) <= 1e-6) return false;
          return Math.abs(resolved - sec) <= frame + 1e-6;
        };
        if (
          typeof cue.startSec === 'number' &&
          !sameNumber(cue.startSec, before.startSec) &&
          timeAnchorsEqual(cue.anchor, before.anchor) &&
          !explainedByAnchor(cue.anchor, cue.startSec)
        ) {
          result = { ...cue, anchor: anchorForSec(tl, cue.startSec, 'start') };
        } else if (typeof cue.startSec === 'number' && !cue.anchor) {
          result = { ...cue, anchor: anchorForSec(tl, cue.startSec, 'start') };
        }
        if (
          hasEnd &&
          !sameNumber(cue.endSec, before.endSec) &&
          timeAnchorsEqual(cue.anchorEnd, before.anchorEnd) &&
          !(typeof cue.endSec === 'number' && explainedByAnchor(cue.anchorEnd, cue.endSec))
        ) {
          result = { ...(result ?? cue) };
          if (typeof cue.endSec === 'number') result.anchorEnd = anchorForSec(tl, cue.endSec, 'end');
          else delete result.anchorEnd;
        } else if (hasEnd && typeof cue.endSec === 'number' && !cue.anchorEnd) {
          result = { ...(result ?? cue), anchorEnd: anchorForSec(tl, cue.endSec, 'end') };
        }
      }
      if (result) changed = true;
      return result ?? cue;
    });
    return changed ? out : track;
  };
  const narrationTrack = sync(prev.narrationTrack, next.narrationTrack, 'narration');
  const seTrack = sync(prev.seTrack, next.seTrack, 'se');
  const bgmTrack = sync(prev.bgmTrack, next.bgmTrack, 'bgm');
  const overlays = sync(prev.overlays, next.overlays, 'overlay');
  if (
    narrationTrack === next.narrationTrack &&
    seTrack === next.seTrack &&
    bgmTrack === next.bgmTrack &&
    overlays === next.overlays
  ) {
    return next;
  }
  return {
    ...next,
    ...(next.narrationTrack !== undefined ? { narrationTrack } : {}),
    ...(next.seTrack !== undefined ? { seTrack } : {}),
    ...(next.bgmTrack !== undefined ? { bgmTrack } : {}),
    ...(next.overlays !== undefined ? { overlays } : {}),
  };
}

/** 項目が素材秒 `sourceSec` を含むか (edge で境界の帰属を決める。§4.2 末尾)。 */
export function itemContainsSourceSec(item: Pick<TimelineItem, 'inSec' | 'outSec'>, sourceSec: number, edge: AnchorEdge): boolean {
  return edge === 'start'
    ? item.inSec - EPS <= sourceSec && sourceSec < item.outSec - EPS
    : item.inSec + EPS < sourceSec && sourceSec <= item.outSec + EPS;
}

/**
 * 錨 `anchor` (項目 `from` に付いている) を、素材秒を含む候補 `pieces` の項目へ付け替える
 * (通常の分割・ジャンプカット・結合・1 対 N 移植で共通。§3.3.2 / §3.4 / §4.2 末尾)。
 * - 錨の表示位置を `from` の素材秒に直し、edge で含む片を選ぶ (開始: `in <= s < out`、終端: `in < s <= out`)
 * - どの片にも入らなければ最寄りの片の端 (前なら offset 0、後ろなら表示尺)
 * - 候補が無ければ null (呼び出し側がそのまま残す)
 */
export function remapItemAnchorToPieces(
  anchor: Extract<TimeAnchor, { kind: 'item' }>,
  from: TimelineItem,
  pieces: readonly TimelineItem[],
  edge: AnchorEdge,
): TimeAnchor | null {
  if (pieces.length === 0) return null;
  const sourceSec = itemSourceSecAt(from, anchor.offsetSec);
  const hit = pieces.find((piece) => itemContainsSourceSec(piece, sourceSec, edge));
  if (hit) {
    return { kind: 'item', itemId: hit.id, offsetSec: roundSec(itemDisplaySecAtSource(hit, sourceSec)) };
  }
  let best: TimelineItem = pieces[0]!;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const piece of pieces) {
    const distance = sourceSec < piece.inSec ? piece.inSec - sourceSec : sourceSec > piece.outSec ? sourceSec - piece.outSec : 0;
    if (distance < bestDistance) {
      best = piece;
      bestDistance = distance;
    }
  }
  const offsetSec = sourceSec < best.inSec ? 0 : roundSec(itemDisplayDurationSec(best));
  return { kind: 'item', itemId: best.id, offsetSec };
}

function sameInstance(a: TimelineItem, b: TimelineItem): boolean {
  if (a.lineage && b.lineage) return a.lineage.instanceId === b.lineage.instanceId;
  return a.clipId === b.clipId;
}

/**
 * タイムラインの項目の増減 (分割・結合・ジャンプカット・区切り位置の変更) に合わせて錨を付け替える。
 * `prev` の項目に付いていた `item` 錨ごとに:
 * - その項目が `next` にもあり、素材の範囲 (inSec / outSec) が同じ → そのまま (並べ替え・除外など)
 * - 範囲が変わっていても錨の素材秒を今も含む → そのまま (表示オフセットも変えない。トリムで
 *   削られた部分の錨は引き直しの clamp で新しい終わりへ寄る = 現行の挙動)
 * - 含まない / 項目が無い → `next` の同じインスタンス (lineage が無ければ同じ clipId) の項目のうち、
 *   素材秒を含むものへ (`remapItemAnchorToPieces`)。どの片も含まなければそのまま (トリムは引き直しの clamp、
 *   削除は §4.5 の規則に任せる)
 * `tail` / `absolute` は触らない。変化が無ければ同じ参照を返す。
 */
export function remapAnchorsForTimelineChange(prev: EditProject, next: EditProject): EditProject {
  if (prev.timeline === next.timeline) return next;
  const prevById = new Map(prev.timeline.map((item) => [item.id, item]));
  const nextById = new Map(next.timeline.map((item) => [item.id, item]));
  const remap = (anchor: TimeAnchor | undefined, edge: AnchorEdge): TimeAnchor | undefined => {
    if (!anchor || anchor.kind !== 'item') return anchor;
    const from = prevById.get(anchor.itemId);
    if (!from) return anchor;
    const still = nextById.get(anchor.itemId);
    // 項目が素材の同じ範囲のまま残っている (並べ替え・除外 / 採用・他の項目の増減) なら付け替えない。
    // 範囲が同じでも「項目の末尾」の錨 (offset = 表示尺) は start 側の containment から外れるので、ここで
    // 見ないと並べ替えのたびに次の項目の頭へ移ってしまう (実機確認 2026-11-02)
    if (still && still.inSec === from.inSec && still.outSec === from.outSec) return anchor;
    const sourceSec = itemSourceSecAt(from, anchor.offsetSec);
    if (still && itemContainsSourceSec(still, sourceSec, edge)) return anchor;
    const pieces = next.timeline.filter((item) => item.id !== anchor.itemId && sameInstance(from, item));
    const containing = pieces.filter((piece) => itemContainsSourceSec(piece, sourceSec, edge));
    // 素材秒を含む片が無ければ付け替えない: 項目が残っていればトリム (引き直しの clamp で新しい終わりへ)、
    // 無ければ削除 (§4.5: ナレーション / SE は silenced、BGM / オーバーレイは absolute に退避)
    if (containing.length === 0) return anchor;
    return remapItemAnchorToPieces(anchor, from, containing, edge) ?? anchor;
  };
  const apply = <C extends AnchoredCue>(track: C[] | undefined): C[] | undefined => {
    if (!track) return track;
    let changed = false;
    const out = track.map((cue) => {
      const anchor = remap(cue.anchor, 'start');
      const anchorEnd = remap(cue.anchorEnd, 'end');
      if (anchor === cue.anchor && anchorEnd === cue.anchorEnd) return cue;
      changed = true;
      const result = { ...cue };
      if (anchor) result.anchor = anchor;
      if (anchorEnd) result.anchorEnd = anchorEnd;
      return result;
    });
    return changed ? out : track;
  };
  const narrationTrack = apply(next.narrationTrack);
  const seTrack = apply(next.seTrack);
  const bgmTrack = apply(next.bgmTrack);
  const overlays = apply(next.overlays);
  if (
    narrationTrack === next.narrationTrack &&
    seTrack === next.seTrack &&
    bgmTrack === next.bgmTrack &&
    overlays === next.overlays
  ) {
    return next;
  }
  return {
    ...next,
    ...(next.narrationTrack !== undefined ? { narrationTrack } : {}),
    ...(next.seTrack !== undefined ? { seTrack } : {}),
    ...(next.bgmTrack !== undefined ? { bgmTrack } : {}),
    ...(next.overlays !== undefined ? { overlays } : {}),
  };
}

/**
 * 錨の `itemId` を付け替える (移植 §3.3 手順 4: 今の項目 id → 新しい項目 id)。`offsetShiftSec` は
 * 片を畳んだときにその片の相対開始秒を足すためのもの (§3.3.1)。対応の無い id の錨はそのまま。
 */
export function retargetItemAnchors<C extends AnchoredCue>(
  track: C[] | undefined,
  mapping: ReadonlyMap<string, { itemId: string; offsetShiftSec?: number }>,
): C[] | undefined {
  if (!track) return track;
  const move = (anchor: TimeAnchor | undefined): TimeAnchor | undefined => {
    if (!anchor || anchor.kind !== 'item') return anchor;
    const target = mapping.get(anchor.itemId);
    if (!target) return anchor;
    return { kind: 'item', itemId: target.itemId, offsetSec: roundSec(anchor.offsetSec + (target.offsetShiftSec ?? 0)) };
  };
  return track.map((cue) => {
    const anchor = move(cue.anchor);
    const anchorEnd = move(cue.anchorEnd);
    if (anchor === cue.anchor && anchorEnd === cue.anchorEnd) return cue;
    const out = { ...cue };
    if (anchor) out.anchor = anchor;
    if (anchorEnd) out.anchorEnd = anchorEnd;
    return out;
  });
}
